import { create } from 'zustand';
import { 
  Invitation, 
  CreateInvitationRequest, 
  BulkCreateInvitationRequest 
} from '@/types/invitation';
import { invitationService } from '@/lib/api/invitationService';

interface InvitationState {
  teamInvitations: Invitation[];
  userInvitations: Invitation[];
  currentInvitation: Invitation | null;
  isLoading: boolean;
  error: string | null;
  
  createInvitation: (data: CreateInvitationRequest) => Promise<Invitation>;
  bulkCreateInvitations: (data: BulkCreateInvitationRequest) => Promise<Invitation[]>;
  fetchTeamInvitations: (teamId: string) => Promise<void>;
  fetchUserInvitations: () => Promise<void>;
  fetchInvitationByToken: (token: string) => Promise<void>;
  acceptInvitation: (token: string) => Promise<void>;
  declineInvitation: (token: string) => Promise<void>;
  resendInvitation: (invitationId: string) => Promise<void>;
  cancelInvitation: (invitationId: string) => Promise<void>;
}

export const useInvitationStore = create<InvitationState>((set, get) => ({
  teamInvitations: [],
  userInvitations: [],
  currentInvitation: null,
  isLoading: false,
  error: null,
  
  createInvitation: async (data: CreateInvitationRequest) => {
    set({ isLoading: true, error: null });
    try {
      const invitation = await invitationService.createInvitation(data);
      set(state => ({ 
        teamInvitations: [...state.teamInvitations, invitation],
        isLoading: false 
      }));
      return invitation;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  bulkCreateInvitations: async (data: BulkCreateInvitationRequest) => {
    set({ isLoading: true, error: null });
    try {
      const invitations = await invitationService.bulkCreateInvitations(data);
      set(state => ({ 
        teamInvitations: [...state.teamInvitations, ...invitations],
        isLoading: false 
      }));
      return invitations;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  fetchTeamInvitations: async (teamId: string) => {
    set({ isLoading: true, error: null });
    try {
      const invitations = await invitationService.getTeamInvitations(teamId);
      set({ teamInvitations: invitations, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
    }
  },
  
  fetchUserInvitations: async () => {
    set({ isLoading: true, error: null });
    try {
      const invitations = await invitationService.getUserInvitations();
      set({ userInvitations: invitations, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
    }
  },
  
  fetchInvitationByToken: async (token: string) => {
    set({ isLoading: true, error: null, currentInvitation: null });
    try {
      const invitation = await invitationService.getInvitationByToken(token);
      set({ currentInvitation: invitation, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
    }
  },
  
  acceptInvitation: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      await invitationService.acceptInvitation({ token });
      
      // Update current invitation if applicable
      const { currentInvitation } = get();
      if (currentInvitation && currentInvitation.token === token) {
        set({ 
          currentInvitation: { ...currentInvitation, status: 'accepted' } 
        });
      }
      
      // Update user invitations
      set(state => ({
        userInvitations: state.userInvitations.filter(inv => inv.token !== token),
        isLoading: false
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  declineInvitation: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      await invitationService.declineInvitation(token);
      
      // Update current invitation if applicable
      const { currentInvitation } = get();
      if (currentInvitation && currentInvitation.token === token) {
        set({ 
          currentInvitation: { ...currentInvitation, status: 'declined' } 
        });
      }
      
      // Update user invitations
      set(state => ({
        userInvitations: state.userInvitations.filter(inv => inv.token !== token),
        isLoading: false
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  resendInvitation: async (invitationId: string) => {
    set({ isLoading: true, error: null });
    try {
      await invitationService.resendInvitation(invitationId);
      
      // Refresh team invitations after resend
      const { teamInvitations } = get();
      if (teamInvitations.length > 0) {
        const teamId = teamInvitations[0].team_id;
        await get().fetchTeamInvitations(teamId);
      }
      
      set({ isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  cancelInvitation: async (invitationId: string) => {
    set({ isLoading: true, error: null });
    try {
      await invitationService.cancelInvitation(invitationId);
      
      // Update team invitations
      set(state => ({
        teamInvitations: state.teamInvitations.filter(inv => inv.id !== invitationId),
        isLoading: false
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  }
})); 